import { useEffect, useState } from "react";
import { Spinner, Box } from "@chakra-ui/react";
import { fileTypeFromBuffer } from "file-type";

import BaseTemplate from "../../components/BaseTemplate";
import Viewer from "./Viewer";
import { useApi } from "../../hooks/useApi";
import { FileType } from "../../types";

type ViewerProps = {
    id: string;
};

function destructurePath(doc_path: string) {
    const withoutDrive = doc_path
        .replace(/^Z:\\|^Z:\//, "")
        .replace(/^P:\\|^P:\//, "");

    const lastSlashIndex = withoutDrive.lastIndexOf("\\");
    const directory = withoutDrive.substring(0, lastSlashIndex);
    const filename = withoutDrive.substring(lastSlashIndex + 1);

    return { dir: directory, filename };
}

export default function FileViewerPage({ id }: ViewerProps) {
    const { downloadFileMagicFiler, getFile } = useApi();

    const [file, setFile] = useState<FileType | null>(null);
    const [fileBlob, setFileBlob] = useState<Blob | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);

    // 1) Load file metadata
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const f = await getFile(id);
                if (!cancelled) setFile(f);
            } catch (e) {
                console.error("Error fetching file:", e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [id, getFile]);

    // 2) Download file once we have doc_path
    useEffect(() => {
        if (!file?.doc_path) return;

        let cancelled = false;

        (async () => {
            try {
                const { dir, filename } = destructurePath(file.doc_path);
                if (!dir || !filename) return;

                const bytes = await downloadFileMagicFiler(dir, filename);
                const mime = (await fileTypeFromBuffer(bytes))?.mime ?? "application/octet-stream";
                const blob = new Blob([bytes], { type: mime });

                if (!cancelled) {
                    setFileBlob(blob);
                    setFileName(filename);
                }
            } catch (e) {
                console.error("Download failed:", e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [file?.doc_path, downloadFileMagicFiler]);

    if (!file || !fileBlob || !fileName) {
        return <Spinner size="xl" mt={10} />;
    }

    return (
        <BaseTemplate>
            <BaseTemplate.Body>
                <Box h="100%">
                    <Viewer fileBlob={fileBlob} fileName={file.eb_filename} />
                </Box>
            </BaseTemplate.Body>
        </BaseTemplate>
    );
}
